'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import emptyFunction from 'react-utils/emptyFunction';
import CodeMirror from 'codemirror';
import OptionManager from 'react-codemirror/util/OptionManager';
import { isPropsEqual } from 'react-codemirror/util/Props';
import loadCSS from 'react-codemirror/decorator/_internal/loadCSS';
import { compose } from './util/miniUnderscore.js';
import { jsx as _jsx } from "react/jsx-runtime";
const withLoadCSS = loadCSS();

/**
 * @class
 */
class ReactCodeMirror extends Component {
  constructor(props) {
    super(props);

    /**
     * CodeMirror instance
     * @type {Object}
     */
    this.codemirror = null;
    this.onChange = this.onChange.bind(this);
    this.onCursorActivity = this.onCursorActivity.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.refTextarea = this.refTextarea.bind(this);
  }
  componentDidMount() {
    const config = OptionManager.getOptionsFromProps(this.props);
    this.codemirror = CodeMirror.fromTextArea(this.textarea, config);
    this.codemirror.operation(() => {
      // Initial props like cursor position get added to history
      this.codemirror.clearHistory();
      OptionManager.setWrapperElementStyles(this.codemirror, this.props);
      this.codemirror.setSize(this.props.width, this.props.height);
      this.bindEvents();
      if (this.props.autofocus) {
        // autofocus doesn't always work.
        this.codemirror.focus();
      }
      this.props.refCodemirror(this);
    });
  }
  componentDidUpdate(prevProps) {
    this.props.refCodemirror(this);
    this.codemirror.operation(() => {
      if (this.props.value !== this.codemirror.getValue()) {
        const {
          left,
          top
        } = this.codemirror.getScrollInfo();
        const selections = this.codemirror.listSelections();
        this.codemirror.setValue(this.props.value || '');
        this.codemirror.scrollTo(left, top);
        this.codemirror.setSelections(selections, null, {
          scroll: false
        });
      }
      if (!isPropsEqual(prevProps, this.props, ['width', 'height'])) {
        this.codemirror.setSize(this.props.width, this.props.height);
      }
      OptionManager.setOptionsFromProps(this.codemirror, this.props);
    });
  }
  componentWillUnmount() {
    this.unbindEvents();
    this.codemirror.toTextArea();
    this.codemirror = null;
    this.props.refCodemirror(null);
  }

  /*
   * Events
   */

  bindEvents(bind = true) {
    const method = bind ? 'on' : 'off';
    this.codemirror[method]('change', this.onChange);
    this.codemirror[method]('cursorActivity', this.onCursorActivity);
    this.codemirror[method]('focus', this.onFocus);
    this.codemirror[method]('blur', this.onBlur);
    this.codemirror[method]('scroll', this.onScroll);
  }
  unbindEvents() {
    this.bindEvents(false);
  }
  onChange(codemirror, changeObj) {
    const {
      onChange
    } = this.props;
    if (!onChange) {
      return;
    }
    const value = codemirror.getValue();
    onChange({
      codemirror,
      value,
      changeObj
    });
  }
  onCursorActivity(codemirror) {
    if (!this.props.onCursorActivity) {
      return;
    }
    const cursor = codemirror.getCursor();
    const {
      sel: selection
    } = codemirror.doc;
    this.props.onCursorActivity({
      codemirror,
      cursor,
      selection
    });
  }
  focusHandler(method, codemirror) {
    const handler = this.props[method];
    if (!handler) {
      return;
    }
    handler({
      codemirror
    });
  }
  onFocus(codemirror) {
    this.focusHandler('onFocus', codemirror);
  }
  onBlur(codemirror) {
    this.focusHandler('onBlur', codemirror);
  }
  onScroll(codemirror) {
    if (!this.props.onScroll) {
      return;
    }
    const scrollInfo = codemirror.getScrollInfo();
    this.props.onScroll({
      codemirror,
      scrollInfo
    });
  }

  /*
   * Render
   */

  refTextarea(ref) {
    this.textarea = ref;
  }
  render() {
    const className = classNames('react-codemirror', this.props.className);
    return /*#__PURE__*/_jsx("div", {
      className: className,
      children: /*#__PURE__*/_jsx("textarea", {
        ref: this.refTextarea,
        value: this.props.value,
        readOnly: true
      })
    });
  }
}

/*
 * Statics
 */

ReactCodeMirror.propTypes = Object.assign({}, OptionManager.propTypes, {
  className: PropTypes.string,
  value: PropTypes.string,
  /** @see {@link http://codemirror.net/5/doc/manual.html#setSize} */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** @see {@link http://codemirror.net/5/doc/manual.html#setSize} */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * CodeMirror events. We're only exposing typical use events.
   * Others must be set using the codemirror instance, which can be had via a ref.
   * @see {@link http://codemirror.net/5/doc/manual.html#events}
   */
  onChange: PropTypes.func,
  onCursorActivity: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onScroll: PropTypes.func,
  /**
   * Function ref that can be used instead of `ref` when this component is wrapped by HOCs.
   * @see {@link https://reactjs.org/5/docs/higher-order-components.html#refs-arent-passed-through}
   */
  refCodemirror: PropTypes.func
});
ReactCodeMirror.defaultProps = Object.assign({}, OptionManager.defaultProps, {
  refCodemirror: emptyFunction,
  height: null,
  width: null
});
ReactCodeMirror.displayName = ReactCodeMirror.name;
const modesDeprecationMessage = 'DEPRECATED: `{ Modes} from react-codemirror`\
  \n\tModes can be loaded at anytime making this unreliable.\
  \n\tInspect `CodeMirror.modes` and `CodeMirror.mimeModes` at runtime instead.\
  \n\t`react-codemirror` validates the `mode` prop against these when passed.';

// TODO: Remove
Object.defineProperties(ReactCodeMirror, {
  Modes: {
    enumerable: true,
    get() {
      console.warn(modesDeprecationMessage);
      /* eslint-enable no-console */
      return [...Object.keys(CodeMirror.modes), ...Object.keys(CodeMirror.mimeModes)].reduce((coll, mode) => {
        coll[mode] = mode;
        return coll;
      }, {});
    }
  }
});
export default compose(withLoadCSS)(ReactCodeMirror);